import { styled } from "goober";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { DragIndicator } from "../DragIndicator";
import { PressButton } from "../PressButton";
import { Resizable } from "../Resizable";
import { sidePanelStyles } from "../sidePanelStyles";
import { chatpadRailWidth } from "./Chatpad";

const Workspace = styled("div")`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
  min-height: 0;
`;

const OutputPane = styled("section")`
  display: flex;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  padding: 12px;
  box-sizing: border-box;

  &[hidden] {
    display: none;
  }

  &[data-overlay="false"] {
    padding-left: 0;
  }

  &[data-overlay="true"] {
    ${sidePanelStyles}
    position: absolute;
    top: 12px;
    right: 12px;
    bottom: 12px;
    width: min(480px, calc(100% - 24px));
    height: auto;
    padding: 0;
    border-radius: 12px;
    overflow: hidden;
    z-index: 2;
  }
`;

const OverlayDismiss = styled(PressButton)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  cursor: default;
`;

// Zero-width so there's no gutter: the output pane butts against the chat and
// the grip zone overflows into the output pane's own left edge.
const ResizeHandle = styled("div")`
  position: relative;
  width: 0;
  height: 100%;
`;

const ResizeGripZone = styled("div")`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ew-resize;
`;

export function ChatWorkspaceLayout({
  children,
  panel,
  open,
  onClose,
}: {
  children: ReactNode;
  panel: ReactNode;
  open: boolean;
  onClose: () => void;
}) {
  const [workspaceWidth, setWorkspaceWidth] = useState<number | null>(null);
  const isOverlay = workspaceWidth !== null && workspaceWidth < 720;
  const workspaceRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWorkspaceWidth(entry.contentRect.width);
    });
    observer.observe(workspace);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!open || !isOverlay) return;
    const panel = panelRef.current;
    const previousFocus = document.activeElement;
    if (!panel?.contains(previousFocus)) panel?.focus({ preventScroll: true });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      event.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (
        previousFocus instanceof HTMLElement &&
        previousFocus.isConnected &&
        (panel?.contains(document.activeElement) ||
          document.activeElement === document.body)
      ) {
        previousFocus.focus({ preventScroll: true });
      }
    };
  }, [open, isOverlay, onClose]);

  return (
    <Workspace ref={workspaceRef} data-overlay={isOverlay}>
      <Resizable
        containerRef={workspaceRef}
        orientation="horizontal"
        minSize={360}
        minOppositeSize={360}
        defaultSize="clamp(360px, 50%, calc(100% - 360px))"
        storageKey="chatSidePanelWidth"
        overrideSize={isOverlay ? 0 : !open ? chatpadRailWidth + 13 : undefined}
        Handle={
          open && panel && !isOverlay ? (
            <ResizeHandle>
              <ResizeGripZone>
                <DragIndicator
                  data-drag-indicator
                  data-orientation="horizontal"
                />
              </ResizeGripZone>
            </ResizeHandle>
          ) : null
        }
        first={children}
        second={
          panel ? (
            <OutputPane
              ref={panelRef}
              aria-label="Side panel"
              tabIndex={open && isOverlay ? -1 : undefined}
              hidden={isOverlay && !open}
              data-overlay={isOverlay}
              data-expanded={open}
            >
              {panel}
            </OutputPane>
          ) : null
        }
      />
      {open && panel && isOverlay && (
        <OverlayDismiss
          aria-label="Dismiss panel"
          tabIndex={-1}
          data-no-pill=""
          onPress={onClose}
          children={null}
        />
      )}
    </Workspace>
  );
}
